/**
 * Everyone else in the room, out loud. The meeting kit brings each person's
 * voice into the browser but leaves playing it to the page (its own UI kit
 * does it with a component of its own), so each voice gets a player of its
 * own here. A browser that refuses sound until the page is touched gets one
 * button that lets it in.
 */
import { type ReactNode, useCallback, useEffect, useRef, useState } from "react";
import type { MeetingFace } from "./meeting";

/** The browser said no to playing sound before the page was touched. */
function refused(err: unknown): boolean {
  return err instanceof DOMException && err.name === "NotAllowedError";
}

/** Send one player's sound to the chosen speaker, where the browser allows it. */
function playOn(el: HTMLAudioElement, speakerId: string): void {
  const sink = (el as HTMLAudioElement & { setSinkId?: (id: string) => Promise<void> }).setSinkId;
  if (!sink) return;
  sink.call(el, speakerId).catch(() => {
    /* a speaker that went: the sound stays where it was */
  });
}

function Voice({
  track,
  speakerId,
  players,
  onRefused,
}: {
  track: MediaStreamTrack;
  speakerId: string;
  players: Set<HTMLAudioElement>;
  onRefused: () => void;
}): ReactNode {
  const audio = useRef<HTMLAudioElement>(null);
  useEffect(() => {
    if (audio.current) playOn(audio.current, speakerId);
  }, [speakerId]);
  useEffect(() => {
    const el = audio.current;
    if (!el) return;
    players.add(el);
    el.srcObject = new MediaStream([track]);
    el.play().catch((err: unknown) => {
      if (refused(err)) onRefused();
    });
    return () => {
      players.delete(el);
      el.srcObject = null;
    };
  }, [track, players, onRefused]);
  return <audio ref={audio} autoPlay playsInline />;
}

export function RoomSound({
  faces,
  soundsOf,
  speakerId = "",
}: {
  faces: readonly MeetingFace[];
  soundsOf: (id: string) => MediaStreamTrack[];
  /** Where the voices play; empty is the system's own choice. */
  speakerId?: string;
}): ReactNode {
  const [blocked, setBlocked] = useState(false);
  const players = useRef(new Set<HTMLAudioElement>()).current;
  const onRefused = useCallback(() => setBlocked(true), []);

  const hear = () => {
    void Promise.all([...players].map((el) => el.play())).then(
      () => setBlocked(false),
      () => {
        /* still refused: the button stays for the next tap */
      },
    );
  };

  const voices = faces
    .filter((f) => !f.mine)
    .flatMap((f) => soundsOf(f.id).map((track) => ({ key: `${f.id}:${track.id}`, track })));

  return (
    <>
      <div className="w-mt-sound" aria-hidden="true">
        {voices.map((v) => (
          <Voice key={v.key} track={v.track} speakerId={speakerId} players={players} onRefused={onRefused} />
        ))}
      </div>
      {blocked ? (
        <button type="button" className="w-mt-hear" onClick={hear}>
          Tap to hear the room
        </button>
      ) : null}
    </>
  );
}
